import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../config/firebase';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { firebaseErrorKey } from '../lib/firebaseErrors';

export default function SignIn() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
      navigate('/dashboard');
    } catch (err) {
      setError(t(firebaseErrorKey(err)));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!email) {
      setError(t('auth.signIn.errors.emailRequired'));
      return;
    }

    setError('');
    setLoading(true);

    try {
      // The email's language follows auth.languageCode, kept in step with the site
      // language by syncAuthLanguage().
      await sendPasswordResetEmail(auth, email.trim());
      setResetSent(true);
      setError('');
    } catch (err) {
      setError(t('auth.signIn.errors.resetFailed', { message: t(firebaseErrorKey(err)) }));
    } finally {
      setLoading(false);
    }
  };

  // Already signed in (e.g. opened /signin from a bookmark): go straight to the dashboard.
  // Not while a sign-in is in flight — handleSubmit navigates itself.
  if (currentUser && !loading) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="signin-container">
      <div className="signin-header">
        <h1>{t('auth.signIn.title')}</h1>
        <p>{t('auth.signIn.subtitle')}</p>
      </div>

      <form onSubmit={handleSubmit} className="signin-form">
        {error && <div className="error-message">{error}</div>}

        <div className="form-group">
          <label htmlFor="email">{t('auth.signIn.email')}</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('auth.signIn.emailPlaceholder')}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="password">{t('auth.signIn.password')}</label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('auth.signIn.passwordPlaceholder')}
            required
          />
        </div>

        <button type="submit" className="btn-submit" disabled={loading}>
          {loading ? t('auth.signIn.submitting') : t('auth.signIn.submit')}
        </button>

        {resetSent && (
          <div className="success-message">
            {t('auth.signIn.resetSent')}
          </div>
        )}

        <button
          type="button"
          onClick={handleResetPassword}
          className="btn-reset"
          disabled={loading}
        >
          {t('auth.signIn.forgotPassword')}
        </button>

        <p className="signup-link">
          {t('auth.signIn.noAccount')} <Link to="/signup">{t('auth.signIn.signUpLink')}</Link>
        </p>
      </form>

      <style>{`
        .signin-container {
          max-width: 400px;
          margin: 60px auto;
          padding: 40px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .signin-header {
          text-align: center;
          margin-bottom: 32px;
        }

        .signin-header h1 {
          font-size: 32px;
          color: #1a1a1a;
          margin-bottom: 8px;
        }

        .signin-header p {
          color: #666;
          font-size: 16px;
        }

        .signin-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .form-group label {
          font-weight: 600;
          color: #333;
          font-size: 14px;
        }

        .form-group input {
          padding: 12px 16px;
          border: 2px solid #e0e0e0;
          border-radius: 8px;
          font-size: 16px;
          transition: border-color 0.2s;
        }

        .form-group input:focus {
          outline: none;
          border-color: #4285f4;
        }

        .btn-submit {
          padding: 14px;
          background: #4285f4;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s;
        }

        .btn-submit:hover:not(:disabled) {
          background: #357ae8;
        }

        .btn-submit:disabled {
          background: #ccc;
          cursor: not-allowed;
        }

        .error-message {
          padding: 12px;
          background: #ffebee;
          color: #c62828;
          border-radius: 8px;
          font-size: 14px;
        }

        .success-message {
          padding: 12px;
          background: #e8f5e9;
          color: #2e7d32;
          border-radius: 8px;
          font-size: 14px;
        }

        .btn-reset {
          padding: 10px;
          background: none;
          color: #4285f4;
          border: none;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          text-decoration: underline;
        }

        .btn-reset:hover:not(:disabled) {
          color: #357ae8;
        }

        .btn-reset:disabled {
          color: #ccc;
          cursor: not-allowed;
        }

        .signup-link {
          text-align: center;
          color: #666;
          font-size: 14px;
        }

        .signup-link a {
          color: #4285f4;
          text-decoration: none;
          font-weight: 600;
        }

        @media (max-width: 640px) {
          .signin-container {
            margin: 24px 16px;
            padding: 24px 20px;
          }

          .signin-header h1 {
            font-size: 26px;
          }
        }
      `}</style>
    </div>
  );
}
